import React, { useState } from 'react';

import type { UUID } from '@aline/common';
import {
  ConfirmationDialog,
  ConfirmationDialogVariant,
} from 'src/components/generic/ConfirmationDialog';
import { useUpdateCommentThreadPrivacy } from 'src/hooks/comment';

export function useConfirmCommentPrivacyChanges(rootCommentId: UUID): {
  confirmationDialog: JSX.Element | null;
  requestThreadPrivacyChange: (nextIsPrivate: boolean) => void;
} {
  const { updateThreadPrivacy } = useUpdateCommentThreadPrivacy(rootCommentId);
  const [pendingIsPrivate, setPendingIsPrivate] = useState<boolean | null>(null);

  const confirmationDialog =
    pendingIsPrivate === null ? null : (
      <ConfirmationDialog
        open
        {...getCommentPrivacyChangeCopy(pendingIsPrivate)}
        variant={ConfirmationDialogVariant.Warn}
        onClickPrimary={async () => {
          await updateThreadPrivacy(pendingIsPrivate);
          setPendingIsPrivate(null);
        }}
        onClose={() => setPendingIsPrivate(null)}
      />
    );

  return { confirmationDialog, requestThreadPrivacyChange: setPendingIsPrivate };
}

function getCommentPrivacyChangeCopy(nextIsPrivate: boolean): {
  message: string;
  primaryText: string;
  title: string;
} {
  if (nextIsPrivate) {
    return {
      message:
        'Only users in your organization will be able to see this thread and all replies. External collaborators will lose access.',
      primaryText: 'Make thread private',
      title: 'Make thread private?',
    };
  }

  return {
    message:
      'All parties with access will be able to see this thread and all replies, including external collaborators.',
    primaryText: 'Make thread public',
    title: 'Make thread public?',
  };
}
